<div class="order-detail">

  <header class="detail-header">
    <div class="detail-heading">
      <span class="detail-order-number">{{ order.orderCode || 'Sin código' }}</span>
      <span class="detail-date">{{ order.date | date: dateFormat }}</span>
    </div>
    <app-order-status-badge [status]="order.status" [shippingType]="order.shippingType" />
  </header>

  <button class="order-detail__back" type="button" (click)="closeDetail()">Volver a pedidos</button>

  @if (canChangeStatus) {
    <section class="order-detail__status-control" aria-label="Actualizar estado del pedido">
      <label for="order-next-status">Cambiar estado</label>
      <select id="order-next-status" #nextStatus [value]="selectedStatus() ?? ''" (change)="selectStatus(nextStatus.value)">
        <option value="">Selecciona el nuevo estado</option>
        @for (status of availableStatuses; track status) {
          <option [value]="status">{{ statusLabel(status) }}</option>
        }
      </select>
      <button type="button" [disabled]="!selectedStatus()" (click)="confirmStatusChange()">Cambiar estado</button>
    </section>
  }

  @if (statusReasonLabel; as reasonLabel) {
    <section class="detail-block detail-status-reason">
      <h3>{{ reasonLabel }}</h3>
      <p [class.detail-status-reason--empty]="!hasStatusReason">{{ statusReasonText }}</p>
    </section>
  }

  @if (isPaymentPending) {
    <p class="detail-warning" role="alert">
      El pago de este pedido todavía no se confirma: el cliente sigue en la plataforma de pago
      o la confirmación aún no llega. No lo despaches hasta que quede pagado.
    </p>
  } @else if (isPaymentUnconfirmed) {
    <p class="detail-warning" role="alert">
      Pago sin confirmar: TUU no avisó el resultado dentro del plazo. Revisa en el Espacio de
      Trabajo de TUU si el pago existe. Si la confirmación llega tarde, el pedido se marcará
      pagado solo.
    </p>
  }

  <section class="detail-panel">
    <h3>Cliente</h3>

    <div class="detail-fields">
      <div class="detail-field">
        <span class="field-label">Nombre</span>
        <span class="field-value detail-strong">{{ order.customer.name || 'Sin nombre' }}</span>
      </div>

      <div class="detail-field">
        <span class="field-label">Correo</span>
        <span class="field-value">{{ order.customer.email || 'Sin correo' }}</span>
      </div>

      <div class="detail-field">
        <span class="field-label">Teléfono</span>
        <span class="field-value">{{ order.customer.phone || 'Sin teléfono' }}</span>
      </div>

      @if (order.customer.rut) {
        <div class="detail-field">
          <span class="field-label">RUT</span>
          <span class="field-value">{{ formatRut(order.customer.rut) }}</span>
        </div>
      }
    </div>
  </section>

  @if (isPickup) {
    <p class="detail-pickup">Retiro en tienda</p>
  } @else {
    <section class="detail-panel">
      <h3>Envío</h3>

      <div class="detail-fields">
        <div class="detail-field">
          <span class="field-label">Dirección</span>
          <span class="field-value detail-strong">{{ streetOf(order.shipping) }}</span>
        </div>

        <div class="detail-field">
          <span class="field-label">Comuna</span>
          <span class="field-value">{{ communeOf(order.shipping) }}</span>
        </div>

        <div class="detail-field">
          <span class="field-label">Región</span>
          <span class="field-value">{{ regionOf(order.shipping) }}</span>
        </div>

        @if (localityOf(order.shipping); as shippingLocality) {
          <div class="detail-field">
            <span class="field-label">Villa o sector</span>
            <span class="field-value">{{ shippingLocality }}</span>
          </div>
        }

        @if (order.trackingCode) {
          <div class="detail-field">
            <span class="field-label">Seguimiento</span>
            <span class="field-value detail-strong">
              @for (trackingLink of trackingLinks; track trackingLink.trackingCode) {
                <span class="tracking-link">
                  {{ trackingLink.trackingCode }}
                  · <a [href]="trackingLink.trackingUrl" target="_blank" rel="noopener noreferrer">Ver en Chilexpress</a>
                </span>
              }
            </span>
          </div>
        }
      </div>
    </section>

    @if (shippingQuote; as quote) {
      <section class="detail-panel chilexpress-panel">
        <div class="chilexpress-panel__header">
          <h3>Datos para Chilexpress</h3>
          <button type="button" class="chilexpress-copy" (click)="copyChilexpressData()">
            @switch (chilexpressCopyState()) {
              @case ('copied') { Copiado }
              @case ('failed') { No se pudo copiar }
              @default { Copiar datos }
            }
          </button>
        </div>

        @if (chilexpressCopyState() === 'failed') {
          <p class="detail-caption" role="alert">El navegador no permitió copiar. Selecciona los datos de abajo y cópialos a mano.</p>
        }

        <div class="detail-fields">
          <div class="detail-field">
            <span class="field-label">Servicio cotizado</span>
            <span class="field-value">{{ quote.serviceName || 'Tarifa de respaldo' }}</span>
          </div>
          <div class="detail-field">
            <span class="field-label">Destinatario</span>
            <span class="field-value">{{ order.customer.name }} · {{ order.customer.phone || 'Sin teléfono' }}</span>
          </div>
        </div>

        <ul class="chilexpress-packages">
          @for (shippingPackage of quote.packages; track $index) {
            <li>
              <strong>Bulto {{ $index + 1 }}: caja {{ shippingPackage.boxLabel }}</strong>
              <span>{{ packageMeasures(shippingPackage) }} · {{ packageWeight(shippingPackage) }} · {{ shippingPackage.unitsCount }} pieza(s)</span>
              <span>Valor declarado: {{ shippingPackage.declaredValue | clpCurrency }}</span>
            </li>
          }
        </ul>

        <p class="detail-caption">
          La caja es la que se usó para cotizar. Si no la tienes, usa una más grande: la diferencia la asume Argentus.
        </p>
      </section>
    }
  }

  @if (invoice; as invoiceData) {
    <section class="detail-panel">
      <h3>Facturación</h3>

      <div class="detail-fields">
        <div class="detail-field">
          <span class="field-label">Razón social</span>
          <span class="field-value detail-strong">{{ invoiceData.businessName || '—' }}</span>
        </div>

        <div class="detail-field">
          <span class="field-label">RUT</span>
          <span class="field-value">{{ formatRut(invoiceData.rut) || '—' }}</span>
        </div>

        <div class="detail-field">
          <span class="field-label">Giro</span>
          <span class="field-value">{{ invoiceData.activity || '—' }}</span>
        </div>
      </div>

      @if (invoiceAddress; as billing) {
        <div class="detail-fields detail-fields--stacked">
          <div class="detail-field">
            <span class="field-label">Dirección de facturación</span>
            <span class="field-value detail-strong">{{ streetOf(billing) }}</span>
          </div>

          <div class="detail-field">
            <span class="field-label">Comuna</span>
            <span class="field-value">{{ communeOf(billing) }}</span>
          </div>

          <div class="detail-field">
            <span class="field-label">Región</span>
            <span class="field-value">{{ regionOf(billing) }}</span>
          </div>

          @if (localityOf(billing); as billingLocality) {
            <div class="detail-field">
              <span class="field-label">Villa o sector</span>
              <span class="field-value">{{ billingLocality }}</span>
            </div>
          }
        </div>

        @if (!isPickup) {
          <p class="detail-caption">El despacho va a la dirección de envío: {{ shippingSummary }}</p>
        }
      } @else {
        <p class="detail-caption">
          @if (isPickup) {
            Sin dirección de facturación registrada.
          } @else {
            Se factura a la misma dirección del envío.
          }
        </p>
      }
    </section>
  }

  <section class="detail-block">
    <h3>Productos</h3>

    <table class="detail-table">
      <thead>
        <tr>
          <th>Producto</th>
          <th>Variante</th>
          <th class="align-center">Cant.</th>
          <th class="align-right">Precio</th>
          <th class="align-right">Subtotal</th>
        </tr>
      </thead>
      <tbody>
        @for (item of order.items; track $index) {
          <tr>
            <td class="detail-strong">{{ item.productName }}</td>
            <td class="detail-muted">{{ itemVariant(item) }}</td>
            <td class="align-center">{{ item.quantity }}</td>
            <td class="align-right">{{ item.unitPrice | clpCurrency }}</td>
            <td class="align-right">{{ item.lineSubtotal | clpCurrency }}</td>
          </tr>
        } @empty {
          <tr>
            <td colspan="5" class="detail-muted">Este pedido no tiene productos registrados.</td>
          </tr>
        }
      </tbody>
    </table>
  </section>

  <section class="detail-totals">
    <div class="totals-row">
      <span>Subtotal</span>
      <span>{{ order.totals.subtotal | clpCurrency }}</span>
    </div>
    <div class="totals-row">
      <span>Envío</span>
      <span>
        @if (order.totals.shippingCost > 0) {
          {{ order.totals.shippingCost | clpCurrency }}
        } @else {
          Gratis
        }
      </span>
    </div>
    @if (order.totals.taxAmount !== null) {
      <div class="totals-row">
        <span>IVA (19%, incluido)</span>
        <span>{{ order.totals.taxAmount | clpCurrency }}</span>
      </div>
    }
    <div class="totals-row totals-row--grand">
      <span>Total</span>
      <span>{{ order.totals.total | clpCurrency }}</span>
    </div>
  </section>

  @if (order.notes) {
    <section class="detail-block detail-notes">
      <h3>Notas</h3>
      <p>{{ order.notes }}</p>
    </section>
  }

</div>
